<template>
  <v-card flat class="pa-0">
    <!-- Panel Başlığı -->
    <v-toolbar flat dense>
      <v-toolbar-title class="subtitle-1 font-weight-bold">Seçilebilir Ücretler</v-toolbar-title>
      <v-spacer></v-spacer>
      <div class="caption grey--text">{{ sortedFares.length }} seçenek</div>
    </v-toolbar>
    <v-divider></v-divider>

    <!-- Uçuş kısa bilgi (opsiyonel) -->
    <div class="px-4 pt-3 pb-1 caption grey--text text--darken-1" v-if="segment">
      {{ segment.DepartureAirportCode }} → {{ segment.ArrivalAirportCode }}
      • {{ segment.DepartureDate }} {{ segment.DepartureTime }}
      • {{ segment.JourneyDurationInMinute }} dk •
      {{ segment.MarkettingAirline || segment.OperatingAirline }} {{ segment.FlightNumber }}
    </div>

    <!-- Kart Listesi -->
    <v-container fluid class="pt-2">
      <v-row dense>
        <v-col
          v-for="(fare, idx) in sortedFares"
          :key="fare.FareCode || idx"
          cols="12" sm="6" md="4" lg="4" xl="3"
        >
          <v-card outlined class="fare-card fill-height d-flex flex-column">
            <!-- ÜST SATIR: Paket adı (sol) + Toplam fiyat (sağ) -->
            <v-card-title class="py-3 justify-space-between align-center">
              <div class="d-flex align-center">
                <span class="font-weight-bold">{{ fare.FareName }}</span>
                <v-chip v-if="isFlex(fare)" x-small class="ml-2" color="blue" text-color="white" label>
                  Flex
                </v-chip>
              </div>
              <div class="text-right">
                <div class="headline font-weight-bold">
                  {{ money(fare.TotalFare, fare.Currency) }}
                </div>
                <div v-if="fare.FareCurrencies" class="caption grey--text">
                  USD ~ {{ fare.FareCurrencies.USD }} • EUR ~ {{ fare.FareCurrencies.EUR }}
                </div>
              </div>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Özellikler -->
            <v-card-text class="py-3">
              <div class="feature-row">
                <span class="feature-label">Kabin</span>
                <span class="feature-value">
                  {{ fare.Cabin || 'Economy' }} ({{ fare.BookingClass || segment.BookingClass || '—' }})
                </span>
              </div>

              <div class="feature-row">
                <span class="feature-label">Bagaj</span>
                <span class="feature-value">
                  <template v-if="fare.CheckedBaggageAllowance">
                    {{ fare.CheckedBaggageAllowance.Kilos }} kg / {{ fare.CheckedBaggageAllowance.Pieces }} parça
                  </template>
                  <template v-else>—</template>
                </span>
              </div>

              <div class="feature-row">
                <span class="feature-label">El Bagajı</span>
                <span class="feature-value">
                  <template v-if="fare.CarryOnBaggageAllowance">
                    {{ fare.CarryOnBaggageAllowance.Kilos || '—' }} kg
                  </template>
                  <template v-else>—</template>
                </span>
              </div>

              <v-divider class="my-2"></v-divider>

              <div class="feature-row">
                <span class="feature-label">Taban Fiyat</span>
                <span class="feature-value">{{ money(fare.BaseFare, fare.Currency) }}</span>
              </div>

              <div class="feature-row">
                <span class="feature-label">Vergiler</span>
                <span class="feature-value">
                  <template v-if="fare.Taxes && fare.Taxes.length">
                    {{ money(sumTaxes(fare), fare.Currency) }}
                  </template>
                  <template v-else>—</template>
                </span>
              </div>

              <div class="feature-row">
                <span class="feature-label">Paket Ücreti</span>
                <span class="feature-value">
                  <template v-if="fare.PackagePrice"> {{ money(fare.PackagePrice, fare.Currency) }} </template>
                  <template v-else>—</template>
                </span>
              </div>

              <div class="feature-row">
                <span class="feature-label">Hizmet / Sağlayıcı</span>
                <span class="feature-value">
                  {{ money(fare.ServiceFee || 0, fare.Currency) }} / {{ money(fare.ProviderFee || 0, fare.Currency) }}
                </span>
              </div>

              <!-- Değişiklik / İptal Koşulları -->
              <v-divider class="my-3" v-if="hasAnyPenalty(fare)"></v-divider>
              <div v-if="hasAnyPenalty(fare)">
                <div class="subtitle-2 font-weight-bold mb-1">Değişiklik/İptal Koşulları</div>

                <div v-if="fare.Penalties && fare.Penalties.ChangePenalties && fare.Penalties.ChangePenalties.length">
                  <div class="overline mb-1">Değişiklik</div>
                  <ul class="bullet-list">
                    <li v-for="(p,i) in fare.Penalties.ChangePenalties" :key="'chg'+i">
                      {{ formatPenaltyLine(p) }}
                    </li>
                  </ul>
                </div>

                <div v-if="fare.Penalties && fare.Penalties.CancellationPenalties && fare.Penalties.CancellationPenalties.length">
                  <div class="overline mb-1">İptal</div>
                  <ul class="bullet-list">
                    <li v-for="(p,i) in fare.Penalties.CancellationPenalties" :key="'can'+i">
                      {{ formatPenaltyLine(p) }}
                    </li>
                  </ul>
                </div>
              </div>
            </v-card-text>

           <v-spacer></v-spacer>

<v-card-actions class="pa-3 d-flex justify-center">
  <v-btn color="primary" large @click="select(fare)">
    Seç
  </v-btn>
</v-card-actions>
          </v-card>
        </v-col>

        <v-col cols="12" v-if="sortedFares.length === 0">
          <v-alert type="info" border="left" colored-border>Gösterilecek ücret bulunamadı.</v-alert>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
export default {
  name: "FlightFareCards",
  props: { flight: { type: Object, required: true } },
  computed: {
    segment() {
      const seg = (this.flight.FlightSegments && this.flight.FlightSegments[0]) || {};
      return {
        DepartureAirportCode: seg.DepartureAirportCode,
        ArrivalAirportCode: seg.ArrivalAirportCode,
        DepartureDate: seg.DepartureDate,
        DepartureTime: seg.DepartureTime,
        JourneyDurationInMinute: seg.JourneyDurationInMinute,
        MarkettingAirline: seg.MarkettingAirline,
        OperatingAirline: seg.OperatingAirline,
        FlightNumber: seg.FlightNumber,
        BookingClass: seg.BookingClass
      };
    },
    sortedFares() {
      const fares = (this.flight.SelectableFares || []).map(f => ({
        ...f,
        TotalFare: this.safeTotal(f)
      }));
      return fares.sort((a, b) => a.TotalFare - b.TotalFare);
    }
  },
  methods: {
    safeTotal(f) {
      if (typeof f.TotalFare === "number" && !isNaN(f.TotalFare)) return f.TotalFare;
      const base = Number(f.BaseFare || 0);
      const taxes = Array.isArray(f.Taxes) ? f.Taxes.reduce((s, t) => s + Number(t.Amount || 0), 0) : 0;
      const pkg = Number(f.PackagePrice || 0);
      const svc = Number(f.ServiceFee || 0);
      const prov = Number(f.ProviderFee || 0);
      return base + taxes + pkg + svc + prov;
    },
    sumTaxes(f) {
      return Array.isArray(f.Taxes) ? f.Taxes.reduce((s, t) => s + Number(t.Amount || 0), 0) : 0;
    },
    money(val, currency = "TRY") {
      try {
        return new Intl.NumberFormat("tr-TR", { style: "currency", currency }).format(Number(val || 0));
      } catch (_) {
        return `${Number(val || 0).toFixed(2)} ${currency}`;
      }
    },
    isFlex(item) {
      return (item.FareName || "").toUpperCase().includes("FLEX");
    },
    hasAnyPenalty(f) {
      const ch = (f.Penalties && f.Penalties.ChangePenalties) || [];
      const ca = (f.Penalties && f.Penalties.CancellationPenalties) || [];
      return (ch && ch.length > 0) || (ca && ca.length > 0);
    },
    formatPenaltyLine(p) {
      if (!p || typeof p !== "object") return String(p);
      const textLike = p.Description || p.Text || p.RuleText || p.Note;
      if (textLike) return textLike;

      const parts = [];
      const n = v => (v === 0 || v ? Number(v) : null);
      const bh = n(p.BeforeDepartureHours || p.UntilHours || p.EndHour);
      const ah = n(p.AfterDepartureHours || p.FromHours || p.StartHour);
      if (bh !== null && ah !== null) parts.push(`${ah}–${bh} saat arası`);
      else if (bh !== null) parts.push(`Kalkıştan önce ≤ ${bh} saat`);
      else if (ah !== null) parts.push(`Kalkıştan sonra ≥ ${ah} saat`);

      const cur = p.Currency || p.CurrencyCode || "TRY";
      const fee = p.Fee || p.Amount || p.PenaltyAmount;
      const pct = p.Percent || p.Percentage || p.Rate;

      if (fee != null && fee !== "") parts.push(`Ceza: ${this.money(fee, cur)}`);
      else if (pct != null && pct !== "") parts.push(`Ceza: %{{}}`.replace("{{}}", Number(pct)));
      else if (p.NonRefundable === true) parts.push("İade yok (Non-refundable)");
      else if (p.Allowed === false) parts.push("İzin verilmiyor");

      return parts.length ? parts.join(" • ") : "Koşul: detay verilmemiş";
    },
    select(fare) {
      this.$emit("select", { flight: this.flight, fare });
    }
  }
};
</script>

<style scoped>
.fare-card { border-radius: 14px; }
.feature-row { display:flex; justify-content:space-between; align-items:baseline; padding:2px 0; }
.feature-label { font-weight:600; color:#6b6b6b; }
.feature-value { text-align:right; }
.bullet-list { margin: 0 0 8px 16px; padding-left: 12px; }
.bullet-list li { margin: 2px 0; }
</style>
